import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";
import errMsg from "../../services/errMsg";

export const fetchCompanies = createAsyncThunk(
  "companies/fetch",
  async (params = {}, { rejectWithValue }) => {
    try {
      // `append` is a client-only flag handled by the reducer; strip from API params
      const { append, ...apiParams } = params;
      const { data } = await api.get("/companies", { params: apiParams });
      return data.data;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Failed to load companies"));
    }
  }
);

export const fetchCompanyStatuses = createAsyncThunk(
  "companies/statuses",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get("/companies/statuses");
      return data.data.statuses;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Failed to load statuses"));
    }
  }
);

export const fetchCompany = createAsyncThunk(
  "companies/fetchOne",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/companies/${id}`);
      return data.data.company;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Failed to load company"));
    }
  }
);

export const createCompany = createAsyncThunk(
  "companies/create",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await api.post("/companies", payload);
      return data.data.company;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Failed to create company"));
    }
  }
);

export const updateCompany = createAsyncThunk(
  "companies/update",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`/companies/${id}`, payload);
      return data.data.company;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Failed to update company"));
    }
  }
);

export const deleteCompany = createAsyncThunk(
  "companies/delete",
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/companies/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Failed to delete company"));
    }
  }
);

export const uploadCompanyPhotos = createAsyncThunk(
  "companies/uploadPhotos",
  async ({ id, type, files }, { rejectWithValue }) => {
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("photos", f));
      const { data } = await api.post(`/companies/${id}/photos/${type}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data.data.company;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Photo upload failed"));
    }
  }
);

export const deleteCompanyPhoto = createAsyncThunk(
  "companies/deletePhoto",
  async ({ id, type, filename }, { rejectWithValue }) => {
    try {
      const { data } = await api.delete(
        `/companies/${id}/photos/${type}/${encodeURIComponent(filename)}`
      );
      return data.data.company;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Failed to remove photo"));
    }
  }
);

const initialState = {
  items: [],
  pagination: { page: 1, limit: 25, total: 0, pages: 0 },
  statuses: [],
  query: {
    page: 1,
    limit: 25,
    search: "",
    status: "live",
    sort: "-createdAt",
    view: "all", // "all" | "favorites" — defaults to All on each app load
  },
  loading: false,
  saving: false,
  uploadingPhotos: false,
  error: null,
  current: null,
  loadingCurrent: false,
};

const replaceItem = (state, company) => {
  const idx = state.items.findIndex((i) => i._id === company._id);
  if (idx >= 0) state.items[idx] = company;
};

const slice = createSlice({
  name: "companies",
  initialState,
  reducers: {
    setQuery(state, { payload }) {
      state.query = { ...state.query, ...payload };
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (b) => {
    b.addCase(fetchCompanies.pending, (s) => {
      s.loading = true;
      s.error = null;
    })
      .addCase(fetchCompanies.fulfilled, (s, a) => {
        s.loading = false;
        if (a.meta.arg?.append) {
          // Append new page's items (mobile infinite scroll). De-dupe by _id
          // in case the same record sneaks in from concurrent requests.
          const existing = new Set(s.items.map((it) => it._id));
          const fresh = a.payload.items.filter(
            (it) => !existing.has(it._id)
          );
          s.items = [...s.items, ...fresh];
        } else {
          s.items = a.payload.items;
        }
        s.pagination = a.payload.pagination;
      })
      .addCase(fetchCompanies.rejected, (s, a) => {
        s.loading = false;
        s.error = a.payload;
      })
      .addCase(fetchCompanyStatuses.fulfilled, (s, a) => {
        s.statuses = a.payload;
      })
      .addCase(fetchCompany.pending, (s) => {
        s.loadingCurrent = true;
        s.error = null;
      })
      .addCase(fetchCompany.fulfilled, (s, a) => {
        s.loadingCurrent = false;
        s.current = a.payload;
        replaceItem(s, a.payload);
      })
      .addCase(fetchCompany.rejected, (s, a) => {
        s.loadingCurrent = false;
        s.error = a.payload;
      })
      .addCase(createCompany.pending, (s) => {
        s.saving = true;
      })
      .addCase(createCompany.fulfilled, (s) => {
        s.saving = false;
      })
      .addCase(createCompany.rejected, (s, a) => {
        s.saving = false;
        s.error = a.payload;
      })
      .addCase(updateCompany.pending, (s) => {
        s.saving = true;
      })
      .addCase(updateCompany.fulfilled, (s, a) => {
        s.saving = false;
        replaceItem(s, a.payload);
        if (s.current?._id === a.payload._id) s.current = a.payload;
      })
      .addCase(updateCompany.rejected, (s, a) => {
        s.saving = false;
        s.error = a.payload;
      })
      .addCase(deleteCompany.fulfilled, (s, a) => {
        s.items = s.items.filter((i) => i._id !== a.payload);
        s.pagination.total = Math.max(0, s.pagination.total - 1);
      })
      .addCase(deleteCompany.rejected, (s, a) => {
        s.error = a.payload;
      })
      .addCase(uploadCompanyPhotos.pending, (s) => {
        s.uploadingPhotos = true;
      })
      .addCase(uploadCompanyPhotos.fulfilled, (s, a) => {
        s.uploadingPhotos = false;
        replaceItem(s, a.payload);
        if (s.current?._id === a.payload._id) s.current = a.payload;
      })
      .addCase(uploadCompanyPhotos.rejected, (s, a) => {
        s.uploadingPhotos = false;
        s.error = a.payload;
      })
      .addCase(deleteCompanyPhoto.fulfilled, (s, a) => {
        replaceItem(s, a.payload);
        if (s.current?._id === a.payload._id) s.current = a.payload;
      })
      .addCase(deleteCompanyPhoto.rejected, (s, a) => {
        s.error = a.payload;
      });
  },
});

export const { setQuery, clearError } = slice.actions;
export default slice.reducer;
